import { css } from "@emotion/css";
import type { TooltipPosition } from "@octopusdeploy/design-system-components";
import { DeprecatedIconButton, LegacyListItemButton, ThirdPartyLogo, Tooltip } from "@octopusdeploy/design-system-components";
import cn from "classnames";
import * as React from "react";
import MediaQuery from "react-responsive";
import { resolveUrl } from "~/utils/resolveUrl";
import { ProcessActionErrorIndicator } from "../ErrorIndicators/ProcessActionErrorIndicator";
import { ProcessActionWarningIndicator } from "../ErrorIndicators/ProcessActionWarningIndicator";
import type { CommonProcessListItemProps } from "./ProcessListItem";
import styles from "./ProcessListItemForSidebar.module.less";
interface ProcessListItemForSidebarProps extends CommonProcessListItemProps {
    className?: string;
    isCurrentAction: boolean;
    onShowContextMenu?: React.MouseEventHandler;
    onClick: () => void;
}
export const toolTipBreakpoint = "1279px";
const renderLogoOrIcon = (actionType: string | JSX.Element, logoUrl: string | undefined, isDisabled: boolean, icon: JSX.Element | undefined, toolTipPosition: TooltipPosition | undefined) => {
    return (<Tooltip content={actionType} position={toolTipPosition}>
            <div className={styles.stepLogo}>{logoUrl ? <ThirdPartyLogo url={resolveUrl(logoUrl)} isDisabled={isDisabled} size={"2rem"} className={styles.logo}/> : icon}</div>
        </Tooltip>);
};
const ProcessListItemForSidebar: React.FC<ProcessListItemForSidebarProps> = ({ name, index, isCurrentAction, isDisabled, isParentGroup, isRunInParallelWithLast, actionErrors, actionWarnings, actionType, logoUrl, icon, onShowContextMenu: onContextMenu, onClick, }) => {
    return (<StepLink isDisabled={isDisabled} isCurrentAction={isCurrentAction} isParentGroup={isParentGroup} onClick={onClick}>
            {isRunInParallelWithLast && (<MediaQuery minWidth={toolTipBreakpoint}>
                    {(matches: boolean) => {
                const toolTipPosition = matches ? "left" : undefined;
                return (<div className={styles.stepWrapper}>
                                <Tooltip content="Runs in parallel" position={toolTipPosition}>
                                    <div className={styles.parallelContainer}>
                                        <div className={styles.parallelIcon}>
                                            <div className={styles.content}>&nbsp;</div>
                                        </div>
                                    </div>
                                </Tooltip>
                                {renderLogoOrIcon(actionType, logoUrl, isDisabled, icon, toolTipPosition)}
                            </div>);
            }}
                </MediaQuery>)}
            {!isRunInParallelWithLast && (<MediaQuery minWidth={toolTipBreakpoint}>
                    {(matches: boolean) => {
                const toolTipPosition = matches ? "left" : undefined;
                return renderLogoOrIcon(actionType, logoUrl, isDisabled, icon, toolTipPosition);
            }}
                </MediaQuery>)}
            {withDisabledTooltipWrapper(<div className={styles.stepContainer}>
                    <div className={styles.stepNumber}>{index}</div>
                    <div className={styles.stepName}>{name}</div>
                </div>, isDisabled)}
            {actionErrors.length > 0 && <ProcessActionErrorIndicator actionErrors={actionErrors}/>}
            {actionWarnings.length > 0 && <ProcessActionWarningIndicator actionWarnings={actionWarnings}/>}
            <DeprecatedIconButton className={styles.overflowButton} icon="EllipsisVerticalIcon" accessibleName={"OverflowMenu"} onClick={onContextMenu}/>
        </StepLink>);
};
ProcessListItemForSidebar.displayName = "ProcessListItemForSidebar"
function withDisabledTooltipWrapper(children: React.ReactNode, isDisabled: boolean) {
    if (isDisabled) {
        return (<Tooltip content="Disabled" className={tooltipStyles}>
                {children}
            </Tooltip>);
    }
    return children;
}
const tooltipStyles = css({
    width: "100%",
});
type StepLinkProps = React.PropsWithChildren<{
    isDisabled: boolean;
    isCurrentAction: boolean;
    isParentGroup: boolean;
    onClick: () => void;
}>;
const StepLinkInternal: React.FC<StepLinkProps> = ({ children, isDisabled, isCurrentAction, isParentGroup, onClick }) => {
    return (<LegacyListItemButton className={cn(styles.listItem, isCurrentAction ? styles.isActive : null, isDisabled ? styles.isDisabled : null, isParentGroup ? styles.isParentGroup : null)} onClick={onClick}>
            <div className={styles.listItemLine}/>
            {children}
        </LegacyListItemButton>);
};
StepLinkInternal.displayName = "StepLinkInternal"
const StepLink = React.memo(StepLinkInternal);
export default React.memo(ProcessListItemForSidebar);
